import { useState, useEffect, useCallback, useMemo } from "react";
import { useTenant } from "../../../hooks/useTenant";
import MemberService from "../../../services/member_service";
import MembershipService from "../../../services/membership_service";
import ActivityService from "../../../services/activity_service";
import EmployeeService from "../../../services/employee_service";
import { INITIAL_FILTERS } from "../constants";
import { SearchHelper } from "../../../utils/helpers";

/**
 * useCrm
 * Hook principal da página de CRM.
 * Carrega dados de referência e aplica filtros acumulativos (AND) sob demanda.
 * A busca só executa ao clicar em "Buscar" (não é em tempo real).
 */
const useCrm = () => {
    const { idTenant } = useTenant();

    // ── Estado Principal ──
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(null); // Filtros que foram efetivamente aplicados

    // ── Dados de Referência (para popular selects) ──
    const [memberships, setMemberships] = useState([]);
    const [activities, setActivities] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loadingRefs, setLoadingRefs] = useState(true);

    // ── Carga dos dados de referência (uma vez) ──
    useEffect(() => {
        if (!idTenant) return;

        const loadRefs = async () => {
            setLoadingRefs(true);
            try {
                const [membershipsData, activitiesData, employeesData] = await Promise.all([
                    MembershipService.getMemberships(idTenant),
                    ActivityService.getActivities(idTenant),
                    EmployeeService.getAllByTenant(idTenant),
                ]);
                setMemberships(membershipsData || []);
                setActivities(activitiesData || []);
                setEmployees(employeesData || []);
            } catch (error) {
                console.error("[useCrm] Erro ao carregar dados de referência:", error);
            } finally {
                setLoadingRefs(false);
            }
        };

        loadRefs();
    }, [idTenant]);

    // ── Handlers de Filtros ──
    const updateFilter = useCallback((field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    }, []);

    const resetFilters = useCallback(() => {
        setFilters(INITIAL_FILTERS);
        setHasSearched(false);
        setAppliedFilters(null);
        setMembers([]);
    }, []);

    // ── Execução da busca (sob demanda) ──
    const executeSearch = useCallback(async () => {
        if (!idTenant) return;

        setLoading(true);
        setHasSearched(true);
        setAppliedFilters({ ...filters });

        try {
            const membersData = await MemberService.getMembers(idTenant);
            setMembers(membersData || []);
        } catch (error) {
            console.error("[useCrm] Erro ao buscar membros:", error);
        } finally {
            setLoading(false);
        }
    }, [idTenant, filters]);

    // ── Aplicação de filtros sobre os membros carregados ──
    const filteredMembers = useMemo(() => {
        if (!appliedFilters || members.length === 0) return [];

        let result = [...members];

        // 1. Busca rápida (nome, email, CPF, telefone)
        if (appliedFilters.search) {
            const normalized = SearchHelper.normalize(appliedFilters.search);
            const searchLower = appliedFilters.search.toLowerCase();
            const searchDigits = appliedFilters.search.replace(/\D/g, "");
            result = result.filter(m => {
                const searchName = m.searchName || SearchHelper.normalize(m.fullName || "");
                return searchName.includes(normalized) ||
                    (m.email || "").toLowerCase().includes(searchLower) ||
                    (searchDigits && (m.document || "").replace(/\D/g, "").includes(searchDigits)) ||
                    (searchDigits && (m.phone || "").replace(/\D/g, "").includes(searchDigits));
            });
        }

        // 2. Situação (status)
        if (appliedFilters.status) {
            result = result.filter(m => m.status === appliedFilters.status);
        }

        // 3. Sexo
        if (appliedFilters.gender) {
            result = result.filter(m => m.gender === appliedFilters.gender);
        }

        // 4. Atividade / Modalidade
        if (appliedFilters.activityId) {
            const selectedActivity = activities.find(a => a.id === appliedFilters.activityId);
            if (selectedActivity) {
                result = result.filter(m => {
                    const memberActivities = m.activityNames || [];
                    return memberActivities.includes(selectedActivity.name);
                });
            }
        }

        // 5. Faixa Etária
        if (appliedFilters.ageMin || appliedFilters.ageMax) {
            const today = new Date();
            result = result.filter(m => {
                if (!m.birthDate) return false;
                const birth = new Date(m.birthDate + "T00:00:00");
                let age = today.getFullYear() - birth.getFullYear();
                const md = today.getMonth() - birth.getMonth();
                if (md < 0 || (md === 0 && today.getDate() < birth.getDate())) age--;
                if (appliedFilters.ageMin && age < parseInt(appliedFilters.ageMin)) return false;
                if (appliedFilters.ageMax && age > parseInt(appliedFilters.ageMax)) return false;
                return true;
            });
        }

        // 6. Professor
        if (appliedFilters.employeeId) {
            result = result.filter(m => m.teacher?.uid === appliedFilters.employeeId);
        }

        // 7. Consultor (Venda)
        if (appliedFilters.consultantId) {
            result = result.filter(m => m.consultant?.uid === appliedFilters.consultantId);
        }

        // 8. Data da Venda — range
        if (appliedFilters.saleStartDate || appliedFilters.saleEndDate) {
            result = result.filter(m => {
                if (!m.firstPurchaseDate) return false;
                const purchaseDate = m.firstPurchaseDate instanceof Date
                    ? m.firstPurchaseDate.toISOString().slice(0, 10)
                    : String(m.firstPurchaseDate).slice(0, 10);
                if (appliedFilters.saleStartDate && purchaseDate < appliedFilters.saleStartDate) return false;
                if (appliedFilters.saleEndDate && purchaseDate > appliedFilters.saleEndDate) return false;
                return true;
            });
        }

        return result;
    }, [members, appliedFilters, activities]);

    return {
        // Dados
        members: filteredMembers,
        totalMembers: members.length,
        loading,
        loadingRefs,
        hasSearched,

        // Filtros
        filters,
        updateFilter,
        resetFilters,
        executeSearch,

        // Dados de Referência (para popular selects)
        memberships,
        activities,
        employees,
    };
};

export default useCrm;
